import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { ROUTES, customToast } from '@common'
import { employeeServices, ErrorTrackingAPI } from '@services'
import { EmployeeLoginComponents } from '@components'

const EmployeeLoginPage = () => {
    const navigate = useNavigate()

    // Check if already authenticated
    useEffect(() => {
        const tokens = employeeServices.getEmployeeTokens()
        const isAuthenticated =
            tokens.accessToken && tokens.accessToken.trim() !== '' && tokens.expiredAt && new Date(tokens.expiredAt) > new Date()

        if (isAuthenticated) {
            navigate(ROUTES.CheckoutAssistant.root, { replace: true })
        }
    }, [navigate])
    const [step, setStep] = useState('email') // 'email' or 'otp'
    const [email, setEmail] = useState('')
    const [otp, setOtp] = useState(['', '', '', '', '', ''])
    const [isLoading, setIsLoading] = useState(false)
    const [isVerifying, setIsVerifying] = useState(false)
    const [error, setError] = useState('')
    const [timeLeft, setTimeLeft] = useState(0)
    const [canResend, setCanResend] = useState(false)
    const [otpExpiry, setOtpExpiry] = useState(null)
    const timerRef = useRef(null)

    // Start countdown timer for OTP
    useEffect(() => {
        let intervalId = null
        if (step === 'otp' && otpExpiry) {
            const updateTimer = () => {
                const now = Date.now()
                const remaining = Math.max(0, Math.floor((otpExpiry - now) / 1000))
                setTimeLeft(remaining)
                setCanResend(remaining === 0)

                if (remaining === 0) {
                    if (timerRef.current) {
                        clearInterval(timerRef.current)
                        timerRef.current = null
                    }
                }
            }

            updateTimer()
            intervalId = setInterval(updateTimer, 1000)
            timerRef.current = intervalId
        }

        return () => {
            if (intervalId) {
                clearInterval(intervalId)
            }

            if (timerRef.current === intervalId) {
                timerRef.current = null
            }
        }
    }, [step, otpExpiry])

    const handleEmailFormSubmit = async data => {
        setIsLoading(true)
        setError('')

        try {
            const result = await employeeServices.retrieveEmployeeOTP(data.email)

            if (result.success) {
                setEmail(data.email)
                // Set OTP expiry time
                const expiryMinutes = result.expiryMinutes || 2
                const expiry = Date.now() + expiryMinutes * 60 * 1000
                setOtpExpiry(expiry)
                setTimeLeft(expiryMinutes * 60)
                setCanResend(false)
                setStep('otp')
                customToast.success('Verification code sent to your email', { duration: 3000 })
            } else {
                setError(result.error || 'Failed to send verification code. Please try again.')
            }
        } catch (err) {
            ErrorTrackingAPI.sendError(err, 'EmployeeLoginPage', 'handleEmailFormSubmit')
            setError('An unexpected error occurred. Please try again.')
        } finally {
            setIsLoading(false)
        }
    }

    const handleOtpFormSubmit = async data => {
        setIsVerifying(true)
        setError('')

        try {
            const userEnteredOtp = otp.join('')

            // Check if OTP is expired
            if (otpExpiry && Date.now() > otpExpiry) {
                setError('OTP has expired. Please request a new one.')
                setIsVerifying(false)
                return
            }

            // Verify OTP code with backend and get access token
            const result = await employeeServices.retrieveEmployeeAccessToken(email, userEnteredOtp)

            if (result.success && result.accessToken) {
                // Set employee context in Sentry
                if (result.employee) {
                    ErrorTrackingAPI.setEmployeeContext(result.employee)
                }
                customToast.success('Login successful!', { duration: 2000 })
                // Navigate to checkout assistant page
                navigate(ROUTES.CheckoutAssistant.root)
            } else {
                setError(result.error || 'Invalid OTP. Please check your code and try again.')
            }
        } catch (err) {
            ErrorTrackingAPI.sendError(err, 'EmployeeLoginPage', 'handleOtpFormSubmit')
            setError('An unexpected error occurred. Please try again.')
        } finally {
            setIsVerifying(false)
        }
    }

    const handleResend = async () => {
        if (!canResend) return

        setCanResend(false)
        setError('')
        setOtp(['', '', '', '', '', ''])

        try {
            const result = await employeeServices.retrieveEmployeeOTP(email)

            if (result.success) {
                const expiryMinutes = result.expiryMinutes || 2
                const expiry = Date.now() + expiryMinutes * 60 * 1000
                setOtpExpiry(expiry)
                setTimeLeft(expiryMinutes * 60)
                customToast.success('OTP resent successfully', { duration: 3000 })
            } else {
                setError(result.error || 'Failed to resend OTP. Please try again.')
                setCanResend(true)
            }
        } catch (err) {
            ErrorTrackingAPI.sendError(err, 'EmployeeLoginPage', 'handleResend')
            setError('An unexpected error occurred. Please try again.')
            setCanResend(true)
        }
    }

    const handleOtpChange = (index, value) => {
        // Only allow single digit (0-9)
        if (!/^\d*$/.test(value) || value.length > 1) return

        const newOtp = [...otp]
        newOtp[index] = value
        setOtp(newOtp)

        // Auto-focus next input
        if (value && index < 5) {
            const nextInput = document.getElementById(`otp-${index + 1}`)
            if (nextInput) nextInput.focus()
        }
    }

    const handleKeyDown = (index, e) => {
        // Handle backspace
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            const prevInput = document.getElementById(`otp-${index - 1}`)
            if (prevInput) prevInput.focus()
        }
    }

    const handlePaste = e => {
        e.preventDefault()
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '')
        if (pastedData.length === 6) {
            const newOtp = pastedData.split('')
            setOtp(newOtp)
        }
    }

    const handleBackToEmail = () => {
        setStep('email')
        setOtp(['', '', '', '', '', ''])
        setError('')
        setOtpExpiry(null)
        setTimeLeft(0)
        setCanResend(false)
        if (timerRef.current) {
            clearInterval(timerRef.current)
            timerRef.current = null
        }
    }

    return (
        <div className="employee-login-page">
            <div className="employee-login-container">
                {step === 'email' ?
                    <EmployeeLoginComponents.EmailStep isLoading={isLoading} error={error} onSubmit={handleEmailFormSubmit} />
                :   <EmployeeLoginComponents.OTPStep
                        email={email}
                        otp={otp}
                        isVerifying={isVerifying}
                        error={error}
                        otpExpiry={otpExpiry}
                        timeLeft={timeLeft}
                        canResend={canResend}
                        onOtpChange={handleOtpChange}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        onSubmit={handleOtpFormSubmit}
                        onResend={handleResend}
                        onBackToEmail={handleBackToEmail}
                    />
                }
            </div>
        </div>
    )
}

export default EmployeeLoginPage
